import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { usePublicClient, useAccount, useWalletClient } from 'wagmi';
import { CONFIG } from '../config';
import { Abi } from 'viem';
import { processUIPageData, isIpfsCid, testIpfsUrl } from '../utils/ipfsUtils';

export default function EntityPage() {
  console.log('EntityPage: Component mounting');
  
  const { entityAddress } = useParams<{ entityAddress: string }>();
  const publicClient = usePublicClient();
  const { address: userAddress } = useAccount();
  const { data: walletClient } = useWalletClient();
  
  console.log('EntityPage: entityAddress from params:', entityAddress);
  
  const [entityData, setEntityData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
  const [pendingWalletRequest, setPendingWalletRequest] = useState<any>(null);
  const [gatewayStatus, setGatewayStatus] = useState<string>('');

  // Handle wallet connection requests from iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Only handle messages from our iframe
      if (event.source !== iframeRef?.contentWindow) return;

      console.log('EntityPage: Received message from iframe:', event.data);

      switch (event.data.type) {
        case 'WALLET_CONNECT_REQUEST':
          try {
            if (!walletClient || !userAddress) {
              // Store the request and trigger wallet connection
              setPendingWalletRequest(event.data);
              
              // Use the global RainbowKit connect modal function
              if (window.openRainbowKitConnectModal) {
                window.openRainbowKitConnectModal();
              } else {
                // Fallback: try to find any button that might be the connect button
                const buttons = document.querySelectorAll('button');
                const connectBtn = Array.from(buttons).find(btn => 
                  btn.textContent?.includes('Connect') || 
                  btn.title?.includes('Connect') ||
                  btn.getAttribute('aria-label')?.includes('Connect')
                );
                if (connectBtn) {
                  connectBtn.click();
                } else {
                  // If we can't find the connect button, send an error response
                  iframeRef?.contentWindow?.postMessage({
                    type: 'WALLET_CONNECT_RESPONSE',
                    success: false,
                    error: 'Please connect your wallet using the connect button in the header'
                  }, '*');
                }
              }
              return;
            }

            // Use the connected account address
            iframeRef?.contentWindow?.postMessage({
              type: 'WALLET_CONNECT_RESPONSE',
              success: true,
              account: userAddress
            }, '*');
          } catch (error) {
            console.error('EntityPage: Wallet connection error:', error);
            iframeRef?.contentWindow?.postMessage({
              type: 'WALLET_CONNECT_RESPONSE',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            }, '*');
          }
          break;

        case 'WALLET_GET_ACCOUNTS_REQUEST':
          try {
            if (!walletClient || !userAddress) {
              iframeRef?.contentWindow?.postMessage({
                type: 'WALLET_GET_ACCOUNTS_RESPONSE',
                success: false,
                error: 'Wallet not connected in parent window'
              }, '*');
              return;
            }

            // Return the connected account
            iframeRef?.contentWindow?.postMessage({
              type: 'WALLET_GET_ACCOUNTS_RESPONSE',
              success: true,
              accounts: [userAddress]
            }, '*');
          } catch (error) {
            console.error('EntityPage: Get accounts error:', error);
            iframeRef?.contentWindow?.postMessage({
              type: 'WALLET_GET_ACCOUNTS_RESPONSE',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            }, '*');
          }
          break;

        case 'WALLET_SEND_TRANSACTION_REQUEST':
          try {
            if (!walletClient) {
              iframeRef?.contentWindow?.postMessage({
                type: 'WALLET_SEND_TRANSACTION_RESPONSE',
                success: false,
                error: 'Wallet not connected in parent window'
              }, '*');
              return;
            }

            const { to, data, value } = event.data.transaction;
            const hash = await walletClient.sendTransaction({
              to,
              data,
              value: value || 0n
            });

            iframeRef?.contentWindow?.postMessage({
              type: 'WALLET_SEND_TRANSACTION_RESPONSE',
              success: true,
              hash: hash
            }, '*');
          } catch (error) {
            console.error('EntityPage: Send transaction error:', error);
            iframeRef?.contentWindow?.postMessage({
              type: 'WALLET_SEND_TRANSACTION_RESPONSE',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            }, '*');
          }
          break;

        case 'PING':
          // Respond to ping messages
          iframeRef?.contentWindow?.postMessage({ type: 'PONG' }, '*');
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [walletClient, userAddress, iframeRef]);

  // Handle wallet connection success
  useEffect(() => {
    if (walletClient && userAddress && pendingWalletRequest) {
      // Wallet is now connected, handle the pending request
      const handlePendingRequest = async () => {
        try {
          // Use the connected account address
          iframeRef?.contentWindow?.postMessage({
            type: 'WALLET_CONNECT_RESPONSE',
            success: true,
            account: userAddress
          }, '*');
        } catch (error) {
          console.error('EntityPage: Pending wallet request error:', error);
          iframeRef?.contentWindow?.postMessage({
            type: 'WALLET_CONNECT_RESPONSE',
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          }, '*');
        }
        setPendingWalletRequest(null);
      };

      handlePendingRequest();
    }
  }, [walletClient, userAddress, pendingWalletRequest, iframeRef]);

  // Load entity data
  useEffect(() => {
    const loadEntityData = async () => {
      if (!entityAddress) return;

      // Validate the address format
      if (!/^0x[a-fA-F0-9]{40}$/.test(entityAddress)) {
        setError('Invalid entity address format');
        setIsLoading(false);
        return;
      }

      try {
        console.log('EntityPage: Starting to load entity data for address:', entityAddress);
        setIsLoading(true);
        setError(null);

        // First, check if the entity contract exists by calling a simple function
        try {
          await publicClient.readContract({
            address: entityAddress as `0x${string}`,
            abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
            functionName: 'owner',
          });
        } catch (error) {
          setError('Entity contract not found at this address. Please check the address and try again.');
          setIsLoading(false);
          return;
        }

        // Get entity data
        console.log('EntityPage: Reading entity contract data...');
        const [name, description, entityType, propertiesAddress] = await Promise.all([
          publicClient.readContract({
            address: entityAddress as `0x${string}`,
            abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
            functionName: 'name',
          }),
          publicClient.readContract({
            address: entityAddress as `0x${string}`,
            abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
            functionName: 'description',
          }),
          publicClient.readContract({
            address: entityAddress as `0x${string}`,
            abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
            functionName: 'entityType',
          }),
          publicClient.readContract({
    address: entityAddress as `0x${string}`,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'properties',
          }),
        ]);

        console.log('EntityPage: Entity data loaded:', { name, description, entityType, propertiesAddress });

        // Get UI page from properties
        let uiPage = '';
        try {
          console.log('EntityPage: Reading UI page from properties contract...');
          uiPage = await publicClient.readContract({
            address: propertiesAddress as `0x${string}`,
            abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
            functionName: 'getUIPage',
            args: [0]
          }) as string;
          console.log('EntityPage: UI page CID (raw):', uiPage);
          console.log('EntityPage: UI page CID length:', uiPage?.length);
          console.log('EntityPage: UI page CID type:', typeof uiPage);
          
          // Check if it's a valid CID
          if (uiPage && isIpfsCid(uiPage)) {
            console.log('EntityPage: Valid IPFS CID detected');
          } else {
            console.log('EntityPage: Not a valid IPFS CID');
          }
        } catch (error) {
          console.warn('EntityPage: No UI page found for entity:', error);
        }

        setEntityData({
          address: entityAddress,
          name,
          description,
          entityType,
          propertiesAddress,
          uiPage
        });

        // Load UI bundle if available
        if (uiPage && isIpfsCid(uiPage)) {
          console.log('EntityPage: Loading UI bundle from IPFS:', uiPage);
          await loadUIBundle(uiPage);
        } else {
          console.log('EntityPage: No valid UI page CID found, skipping UI bundle load');
        }

    } catch (error) {
        console.error('EntityPage: Failed to load entity data:', error);
        setError(`Failed to load entity: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        console.log('EntityPage: Finished loading entity data');
        setIsLoading(false);
      }
    };

    loadEntityData();
  }, [entityAddress, publicClient]);

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);

  const loadUIBundle = async (cid: string) => {
    console.log('EntityPage: Loading UI bundle with CID:', cid);
    
    try {
      setLoading(true);
      setError(null);
      setGatewayStatus('Testing IPFS gateways...');
      
      // Use the utility function to test IPFS URLs with multiple gateways
      const workingUrl = await testIpfsUrl(cid, 'index.html', 5000);
      
      if (workingUrl) {
        console.log('EntityPage: Successfully found working IPFS URL:', workingUrl);
        setGatewayStatus('Loading entity page...');
        setIframeSrc(workingUrl);
        setLoading(false);
      } else {
        console.error('EntityPage: All IPFS gateways failed to load UI bundle');
        setError('Failed to load entity page: All IPFS gateways are unavailable');
        setLoading(false);
      }
    } catch (error: unknown) {
      console.error('EntityPage: Error loading UI bundle:', error);
      setError('Failed to load entity page: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading entity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-xl mb-4">Error Loading Entity</div>
          <p className="text-gray-400 mb-4">{error}</p>
          {error.includes('IPFS gateways') && (
            <div className="text-sm text-gray-500 bg-gray-800 p-3 rounded-lg">
              <p className="mb-2">This entity page is hosted on IPFS and the gateways are currently unavailable.</p>
              <p>This could be due to:</p>
              <ul className="list-disc list-inside mt-2 text-left">
                <li>Network connectivity issues</li>
                <li>IPFS gateways being temporarily down</li>
                <li>The content not being pinned to IPFS</li>
              </ul>
              <p className="mt-2">Please try again later or contact the entity owner.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!entityData) {
      return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-gray-400 text-xl mb-4">Entity not found</div>
          <p className="text-gray-500">The requested entity could not be found.</p>
          </div>
        </div>
      );
    }

  return (
    <div className="min-h-screen bg-gray-900">
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">{gatewayStatus || 'Loading entity page...'}</p>
            {gatewayStatus.includes('Testing IPFS gateways') && (
              <p className="mt-2 text-sm text-gray-500">This may take a few seconds...</p>
            )}
          </div>
        </div>
      ) : iframeSrc ? (
        <iframe
          ref={(el) => setIframeRef(el)}
          src={iframeSrc}
          className="w-full h-screen border-0"
          title="Entity Page"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          onLoad={() => console.log('EntityPage: Iframe loaded successfully')}
          onError={(e) => console.error('EntityPage: Iframe failed to load:', e)}
        />
      ) : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-gray-400 text-xl mb-4">No UI Page</div>
            <p className="text-gray-500">This entity doesn't have a custom UI page.</p>
          </div>
        </div>
      )}
    </div>
  );
} 